'use client';

import React, { useRef, useEffect, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';

const offers = [
  {
    id: 1,
    title: 'Weekend Getaway',
    subtitle: 'Fri–Sun stays up to 30% off',
    image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=600&q=80',
    alt: 'Luxury resort pool perfect for a weekend getaway with friends',
    tag: 'Limited time',
    tagColor: 'bg-[#4088C6]',
    cta: 'Explore deals',
    category: 'weekend',
  },
  {
    id: 2,
    title: 'Early Bird Discount',
    subtitle: 'Book 30+ days ahead, save 20%',
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80',
    alt: 'Serene villa with pool available for early bird booking discounts',
    tag: 'Save 20%',
    tagColor: 'bg-green-500',
    cta: 'Book early',
    category: 'early-bird',
  },
  {
    id: 3,
    title: 'Long Stay Offer',
    subtitle: '7+ nights, 25% discount applied',
    image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600&q=80',
    alt: 'Spacious apartment ideal for long stay guests with all amenities',
    tag: 'Save 25%',
    tagColor: 'bg-blue-500',
    cta: 'Find long stays',
    category: 'long-stay',
  },
  {
    id: 4,
    title: 'Last Minute Deals',
    subtitle: 'Tonight & tomorrow at great prices',
    image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&q=80',
    alt: 'Cozy hotel room available for last minute booking at discounted rates',
    tag: 'Tonight',
    tagColor: 'bg-orange-500',
    cta: 'See availability',
    category: 'last-minute',
  },
];

export default function SpecialOffers() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-10 px-4 sm:px-6 max-w-7xl mx-auto">
      <div className={`mb-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Special offers & deals</h2>
            <p className="text-gray-500 mt-1">Limited-time savings on top-rated stays</p>
          </div>
          <Link href="/special-offers" className="text-sm font-semibold text-gray-900 underline underline-offset-2 hover:text-[#4088C6] transition-colors hidden md:block">
            Show all deals
          </Link>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {offers?.map((offer, i) => (
          <Link
            key={offer?.id}
            href={`/special-offers?category=${offer?.category}`}
            className={`group cursor-pointer transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: `${i * 80}ms` }}
          >
            <div className="relative h-52 rounded-2xl overflow-hidden mb-3">
              <AppImage
                src={offer?.image}
                alt={offer?.alt}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className={`absolute top-3 left-3 ${offer?.tagColor} text-white text-xs font-bold px-2.5 py-1 rounded-full`}>
                {offer?.tag}
              </div>
              <div className="absolute bottom-3 left-3 right-3">
                <p className="text-white font-bold text-sm">{offer?.title}</p>
                <p className="text-white/80 text-xs mt-0.5">{offer?.subtitle}</p>
              </div>
            </div>
            <span className="text-sm font-semibold text-gray-900 hover:text-[#4088C6] transition-colors flex items-center gap-1">
              {offer?.cta}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
